import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { filter } from 'rxjs/operators';

import { Notification } from 'src/app/core/models/core.models';
import { NotificationComponent } from 'src/app/shared/components/notification/notification.component';

/**
 * The app's toasts (WAP-1857, rules in the ticket): success and info auto-dismiss
 * (the global timeOut), error and warning stay until closed because the user has
 * to read and act, and a navigation clears the ones still open so a stale failure
 * never greets the next page. Duplicates are the library's guard (app.config.ts).
 * A toast has no default title (WAP-1844): the tone says what kind it is, a caller
 * passes a title only when it carries information of its own.
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly toastr = inject(ToastrService);
  private readonly persistent = new Set<number>();

  constructor() {
    inject(Router)
      .events.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.clearPersistent());
  }

  showSuccess(message: string, title?: string) {
    this.toastr.success(message, title);
  }

  showError(message: string, title?: string) {
    this.keep(this.toastr.error(message, title, { disableTimeOut: true }));
  }

  showInfo(message: string, title?: string) {
    this.toastr.info(message, title);
  }

  showWarning(message: string, title?: string) {
    this.keep(
      this.toastr.warning(message, title, {
        disableTimeOut: true,
      }),
    );
  }

  /** Closes the error and warning toasts still on screen. */
  clearPersistent(): void {
    for (const id of this.persistent) {
      this.toastr.clear(id);
    }
    this.persistent.clear();
  }

  private keep(toast: ActiveToast<unknown>): void {
    this.persistent.add(toast.toastId);
    toast.onHidden.subscribe(() => this.persistent.delete(toast.toastId));
  }

  showCustomNotification(notification: Notification) {
    // Show the toast and get a reference to it
    const toastRef: ActiveToast<any> = this.toastr.show(
      '',
      'New Notification',
      {
        toastComponent: NotificationComponent,
        closeButton: true,
        enableHtml: true,
        timeOut: 3000,
        extendedTimeOut: 0,
        positionClass: 'toast-bottom-right',
      },
    );

    // 🔹 Get the component reference and manually set the payload
    if (toastRef.toastRef.componentInstance) {
      (toastRef.toastRef.componentInstance as any)['notification'] =
        notification;
      (toastRef.toastRef.componentInstance as any)['toastRef'] =
        toastRef.toastRef;
    }
  }
}
